import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { salesOrderQueryKeys } from '@/lib/services/sales-orders/sales-orders.query'
import type { SalesOrder, SalesOrderIdInput } from '@/lib/services/sales-orders/sales-orders.types'
import { readProperty } from '@/lib/utils'
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useSuspenseQuery,
  type Query,
  type UseQueryOptions,
} from '@tanstack/react-query'
import {
  acceptFsnIncomingOrder,
  acknowledgeFsnIncomingOrderChange,
  checkFsnIncomingOrderAddress,
  checkFsnNetworkToken,
  connectFsnNetworkWithLogin,
  finishFsnIncomingOrderChange,
  getFsnIncomingOrder,
  getFsnIncomingOrders,
  getFsnNetworkIntegration,
  getFsnNetworkSalesOrderFlorists,
  refreshFsnIncomingOrders,
  refreshFsnIncomingOrder,
  refreshFsnNetworkSalesOrder,
  refuseFsnIncomingOrder,
  resetFsnNetworkReadinessRequest,
  retryFsnIncomingOrderImport,
  submitFsnNetworkSalesOrder,
  updateFsnNetworkIntegration,
  type AcceptFsnIncomingOrderInput,
  type AcknowledgeFsnIncomingOrderChangeInput,
  type CheckFsnIncomingOrderAddressInput,
  type FinishFsnIncomingOrderChangeInput,
  type FsnIncomingOrderIdInput,
  type FsnIncomingOrderKeyEntries,
  type FsnIncomingOrderScope,
  type FsnIncomingOrdersKeyEntries,
  type FsnNetworkIntegrationKeyEntries,
  type FsnSalesOrderFloristsKeyEntries,
  type RefuseFsnIncomingOrderInput,
  type SubmitFsnNetworkSalesOrderInput,
} from './fsn-network.api'
import type {
  FsnIncomingOrderFilters,
  FsnIncomingOrdersResponse,
  FsnNetworkIntegrationInput,
  FsnNetworkIntegrationResponse,
  FsnNetworkLoginInput,
} from './fsn-network.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type FsnNetworkIntegrationQueryKey = [AdminQueryRoot & FsnNetworkIntegrationKeyEntries]
type FsnSalesOrderFloristsQueryKey = [AdminQueryRoot & FsnSalesOrderFloristsKeyEntries]
type FsnIncomingOrdersQueryKey = [AdminQueryRoot & FsnIncomingOrdersKeyEntries]
type FsnIncomingOrderQueryKey = [AdminQueryRoot & FsnIncomingOrderKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const fsnNetworkQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'fsnNetwork' }, { organizationId }),
  ],
  integration: ({
    organizationId,
    includeReadiness = false,
  }: FsnNetworkIntegrationKeyEntries): FsnNetworkIntegrationQueryKey =>
    buildScopedAdminQueryKey(fsnNetworkQueryKeys.all({ organizationId })[0], {
      organizationId,
      includeReadiness: Boolean(includeReadiness),
      scope: 'integration',
    }),
  salesOrderFlorists: ({
    organizationId,
    salesOrderId,
  }: FsnSalesOrderFloristsKeyEntries): FsnSalesOrderFloristsQueryKey =>
    buildScopedAdminQueryKey(fsnNetworkQueryKeys.all({ organizationId })[0], {
      organizationId,
      salesOrderId,
      scope: 'sales-order-florists',
    }),
  incomingOrders: ({
    organizationId,
    filters,
  }: FsnIncomingOrdersKeyEntries): FsnIncomingOrdersQueryKey =>
    buildScopedAdminQueryKey(fsnNetworkQueryKeys.all({ organizationId })[0], {
      organizationId,
      filters,
      scope: 'incoming-orders',
    }),
  incomingOrder: ({
    organizationId,
    incomingOrderId,
  }: FsnIncomingOrderKeyEntries): FsnIncomingOrderQueryKey =>
    buildScopedAdminQueryKey(fsnNetworkQueryKeys.all({ organizationId })[0], {
      organizationId,
      incomingOrderId,
      scope: 'incoming-order',
    }),
  // Outside `all`: the check invalidates the inbox when it finishes, which must not start it again.
  incomingOrdersCheck: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'fsnNetworkIncomingOrdersCheck' }, { organizationId }),
  ],
}

export const fsnNetworkQueries = {
  integration: ({ organizationId, includeReadiness = false }: FsnNetworkIntegrationKeyEntries) =>
    queryOptions<
      FsnNetworkIntegrationResponse,
      Error,
      FsnNetworkIntegrationResponse,
      FsnNetworkIntegrationQueryKey
    >({
      queryKey: fsnNetworkQueryKeys.integration({ organizationId, includeReadiness }),
      queryFn: getFsnNetworkIntegration,
    }),
  // The florists hook passes a missing order id and stays disabled until it arrives.
  salesOrderFlorists: ({ organizationId, salesOrderId }: FsnSalesOrderFloristsKeyEntries) =>
    queryOptions({
      queryKey: fsnNetworkQueryKeys.salesOrderFlorists({ organizationId, salesOrderId }),
      queryFn: getFsnNetworkSalesOrderFlorists,
    }),
  incomingOrders: ({ organizationId, filters }: FsnIncomingOrdersKeyEntries) =>
    queryOptions({
      queryKey: fsnNetworkQueryKeys.incomingOrders({ organizationId, filters }),
      queryFn: getFsnIncomingOrders,
    }),
  incomingOrder: ({ organizationId, incomingOrderId }: FsnIncomingOrderScope) =>
    queryOptions({
      queryKey: fsnNetworkQueryKeys.incomingOrder({ organizationId, incomingOrderId }),
      queryFn: getFsnIncomingOrder,
    }),
  // Asks FSN for new orders now instead of waiting for the next poll. A failed check is not
  // retried; the poll keeps running and staff can still check by hand.
  incomingOrdersCheck: ({ organizationId }: { organizationId: number }) =>
    queryOptions({
      queryKey: fsnNetworkQueryKeys.incomingOrdersCheck({ organizationId }),
      queryFn: async () => {
        const result = await refreshFsnIncomingOrders({ organizationId })
        await invalidateIncomingOrderQueries(organizationId, { includeSalesOrders: true })
        return result
      },
      staleTime: 60_000,
      retry: false,
    }),
}

export function useFsnIncomingOrders(
  filters: FsnIncomingOrderFilters = {},
  params: OrganizationScope & QueryParams<ReturnType<typeof fsnNetworkQueries.incomingOrders>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(params.organizationId)

  return useQuery({
    ...fsnNetworkQueries.incomingOrders({ organizationId: resolvedOrganizationId, filters }),
    placeholderData: keepPreviousData,
    ...params,
  })
}

export function useFsnIncomingOrder(
  incomingOrderId: FsnIncomingOrderIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof fsnNetworkQueries.incomingOrder>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...fsnNetworkQueries.incomingOrder({
      organizationId: resolvedOrganizationId,
      incomingOrderId,
    }),
    ...params,
    enabled: Boolean(incomingOrderId) && (params.enabled ?? true),
  })
}

function invalidateIncomingOrderQueries(
  organizationId: number,
  { includeSalesOrders = false } = {}
) {
  const invalidations = [
    queryClient.invalidateQueries({
      queryKey: fsnNetworkQueryKeys.incomingOrders({ organizationId }),
    }),
    queryClient.invalidateQueries({
      queryKey: fsnNetworkQueryKeys.all({ organizationId }),
    }),
  ]

  if (includeSalesOrders) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: salesOrderQueryKeys.all({ organizationId }),
      })
    )
  }

  return Promise.all(invalidations)
}

function isIncomingOrdersQueryForOrganization(query: Query, organizationId: number) {
  const [key] = query.queryKey

  return (
    readProperty(key, 'entity') === 'fsnNetwork' &&
    Number(readProperty(key, 'organizationId')) === Number(organizationId) &&
    readProperty(key, 'scope') === 'incoming-orders'
  )
}

function updateIncomingOrderIntegrationCaches(
  organizationId: number,
  integration: FsnNetworkIntegrationResponse
) {
  const integrationSummary = {
    incoming_orders_enabled: Boolean(integration.incoming_orders_enabled),
    connected: Boolean(integration.connected),
  }

  queryClient.setQueriesData<FsnIncomingOrdersResponse>(
    {
      predicate: (query) => isIncomingOrdersQueryForOrganization(query, organizationId),
    },
    (current) =>
      current
        ? {
            ...current,
            integration: {
              ...current.integration,
              ...integrationSummary,
            },
          }
        : current
  )
}

function syncFsnNetworkIntegrationCaches(
  organizationId: number,
  integration: FsnNetworkIntegrationResponse
) {
  resetFsnNetworkReadinessRequest(organizationId)
  queryClient.setQueryData(fsnNetworkQueryKeys.integration({ organizationId }), integration)
  updateIncomingOrderIntegrationCaches(organizationId, integration)

  return queryClient.invalidateQueries({
    queryKey: fsnNetworkQueryKeys.all({ organizationId }),
  })
}

/**
 * Checks FSN in the background when the Orders Waiting list opens, and again when staff come back
 * to the tab after a minute. Callers show it only as a busy state, never as an error.
 */
export function useFsnIncomingOrdersBackgroundCheck({ enabled }: { enabled: boolean }) {
  const organizationId = useRouteOrganizationId()

  return useQuery({ ...fsnNetworkQueries.incomingOrdersCheck({ organizationId }), enabled })
}

export function useRefreshFsnIncomingOrdersMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: () => refreshFsnIncomingOrders({ organizationId }),
    onSuccess: () => invalidateIncomingOrderQueries(organizationId, { includeSalesOrders: true }),
  })
}

export function useAcceptFsnIncomingOrderMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: AcceptFsnIncomingOrderInput) =>
      acceptFsnIncomingOrder({ organizationId, ...values }),
    onSettled: () => invalidateIncomingOrderQueries(organizationId, { includeSalesOrders: true }),
  })
}

export function useRetryFsnIncomingOrderImportMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ incomingOrderId }: { incomingOrderId: FsnIncomingOrderIdInput }) =>
      retryFsnIncomingOrderImport({ organizationId, incomingOrderId }),
    onSettled: () => invalidateIncomingOrderQueries(organizationId, { includeSalesOrders: true }),
  })
}

export function useRefuseFsnIncomingOrderMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: RefuseFsnIncomingOrderInput) =>
      refuseFsnIncomingOrder({ organizationId, ...values }),
    onSettled: () => invalidateIncomingOrderQueries(organizationId, { includeSalesOrders: true }),
  })
}

export function useCheckFsnIncomingOrderAddressMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ incomingOrderId, address }: CheckFsnIncomingOrderAddressInput) =>
      checkFsnIncomingOrderAddress({ organizationId, incomingOrderId, address }),
  })
}

type FsnNetworkIntegrationParams = OrganizationScope &
  Omit<FsnNetworkIntegrationKeyEntries, 'organizationId'> &
  QueryParams<ReturnType<typeof fsnNetworkQueries.integration>>

/** The order's saved connection summary is visible until the full integration request finishes. */
export type FsnNetworkIntegrationQueryData =
  | FsnNetworkIntegrationResponse
  | NonNullable<SalesOrder['fsn_network_integration']>

type FsnNetworkIntegrationWithPlaceholderParams = OrganizationScope &
  Omit<FsnNetworkIntegrationKeyEntries, 'organizationId'> &
  Partial<
    Omit<
      UseQueryOptions<
        FsnNetworkIntegrationQueryData,
        Error,
        FsnNetworkIntegrationQueryData,
        FsnNetworkIntegrationQueryKey
      >,
      'queryKey' | 'queryFn'
    >
  >

export function useFsnNetworkIntegration({
  organizationId,
  includeReadiness = false,
  ...params
}: FsnNetworkIntegrationWithPlaceholderParams = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)
  const options: Pick<
    ReturnType<typeof fsnNetworkQueries.integration>,
    'queryKey' | 'queryFn'
  > = fsnNetworkQueries.integration({
    organizationId: resolvedOrganizationId,
    includeReadiness,
  })

  return useQuery<
    FsnNetworkIntegrationQueryData,
    Error,
    FsnNetworkIntegrationQueryData,
    FsnNetworkIntegrationQueryKey
  >({
    ...options,
    ...params,
  })
}

export function useSuspenseFsnNetworkIntegration({
  organizationId,
  includeReadiness = false,
  ...params
}: FsnNetworkIntegrationParams = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...fsnNetworkQueries.integration({
      organizationId: resolvedOrganizationId,
      includeReadiness,
    }),
    ...params,
  })
}

export function useFsnNetworkSalesOrderFlorists({
  organizationId,
  salesOrderId,
  enabled = true,
  ...params
}: OrganizationScope & {
  salesOrderId?: SalesOrderIdInput | null
  enabled?: boolean
} & Omit<QueryParams<ReturnType<typeof fsnNetworkQueries.salesOrderFlorists>>, 'enabled'> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...fsnNetworkQueries.salesOrderFlorists({
      organizationId: resolvedOrganizationId,
      salesOrderId,
    }),
    ...params,
    enabled: Boolean(salesOrderId) && enabled,
  })
}

export function useUpdateFsnNetworkIntegrationMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: FsnNetworkIntegrationInput) =>
      updateFsnNetworkIntegration({ organizationId, ...values }),
    onSuccess: (integration) => syncFsnNetworkIntegrationCaches(organizationId, integration),
  })
}

export function useCheckFsnNetworkTokenMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ apiToken }: { apiToken?: string } = {}) =>
      checkFsnNetworkToken({ organizationId, apiToken }),
    onSuccess: (response) => syncFsnNetworkIntegrationCaches(organizationId, response.integration),
  })
}

export function useConnectFsnNetworkLoginMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ username, password }: FsnNetworkLoginInput) =>
      connectFsnNetworkWithLogin({ organizationId, username, password }),
    onSuccess: (integration) => syncFsnNetworkIntegrationCaches(organizationId, integration),
  })
}

function invalidateSalesOrderQueries(organizationId: number, salesOrderId: SalesOrderIdInput) {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: salesOrderQueryKeys.show({ organizationId, salesOrderId }),
    }),
    queryClient.invalidateQueries({
      queryKey: salesOrderQueryKeys.all({ organizationId }),
    }),
  ])
}

export function useSubmitFsnNetworkSalesOrderMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({
      salesOrderId,
      receivingFloristId,
      requestedFlorist,
    }: SubmitFsnNetworkSalesOrderInput) =>
      submitFsnNetworkSalesOrder({
        organizationId,
        salesOrderId,
        receivingFloristId,
        requestedFlorist,
      }),
    onSuccess: (_, { salesOrderId }) => invalidateSalesOrderQueries(organizationId, salesOrderId),
  })
}

export function useRefreshFsnNetworkSalesOrderMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ salesOrderId }: { salesOrderId: SalesOrderIdInput }) =>
      refreshFsnNetworkSalesOrder({ organizationId, salesOrderId }),
    onSuccess: (_, { salesOrderId }) => invalidateSalesOrderQueries(organizationId, salesOrderId),
  })
}

export function useFinishFsnIncomingOrderChangeMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: ({ incomingOrderId, version, review_token }: FinishFsnIncomingOrderChangeInput) =>
      finishFsnIncomingOrderChange({ organizationId, incomingOrderId, version, review_token }),
    onSettled: () => invalidateIncomingOrderQueries(organizationId, { includeSalesOrders: true }),
  })
}

export function useAcknowledgeFsnIncomingOrderChangeMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: ({ incomingOrderId, version }: AcknowledgeFsnIncomingOrderChangeInput) =>
      acknowledgeFsnIncomingOrderChange({ organizationId, incomingOrderId, version }),
    onSettled: () => invalidateIncomingOrderQueries(organizationId, { includeSalesOrders: true }),
  })
}

export function useRefreshFsnIncomingOrderMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: ({ incomingOrderId }: { incomingOrderId: FsnIncomingOrderIdInput }) =>
      refreshFsnIncomingOrder({ organizationId, incomingOrderId }),
    onSettled: () => invalidateIncomingOrderQueries(organizationId, { includeSalesOrders: true }),
  })
}
